import React from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Paper,
    List,
    ListItem,
    ListItemText,
    Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import './ChromeExtensionSetup.scss';

const CHROME_WEB_STORE_EXTENSION_URL = 'https://chromewebstore.google.com/detail/treegress-scenario-builde/bllnchkgoljmgffoipnbjmaacnafhnko';

const ChromeExtensionSetup = () => {
    const navigate = useNavigate();

    // Get current environment based on URL
    const getCurrentEnvironment = () => {
        const hostname = window.location.hostname;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'local';
        } else if (hostname === 'dev-app.treegress.com') {
            return 'dev';
        } else if (hostname === 'sandbox-app.treegress.com') {
            return 'sandbox';
        }
        
        return 'prod';
    };

    const currentEnvironment = getCurrentEnvironment();
    const isProduction = currentEnvironment === 'prod';

    // All available extension options
    const allExtensionOptions = [
        {
            id: 'local',
            name: 'Local Extension',
            description: 'For localhost development'
        },
        {
            id: 'dev',
            name: 'Dev Extension',
            description: 'For dev-app.treegress.com'
        },
        {
            id: 'sandbox',
            name: 'Sandbox Extension',
            description: 'For sandbox-app.treegress.com'
        },
        {
            id: 'prod',
            name: 'Production Extension',
            description: 'For app.treegress.com'
        }
    ];

    // For production: show only Production Extension
    // For other environments: show all options
    const extensionOptions = isProduction 
        ? allExtensionOptions.filter(opt => opt.id === 'prod')
        : allExtensionOptions;

    const handleDownload = async (environment) => {
        try {
            // Get the access token from localStorage
            const token = localStorage.getItem('accessToken');
            
            if (!token) {
                console.error('No access token found');
                alert('Authentication required. Please log in again.');
                return;
            }

            // eslint-disable-next-line no-undef
            const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:4000';
            const url = `${SERVER_URL}/extension/download/${environment}`;
            
            // Make authenticated request to backend API
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to download extension: ${response.statusText}`);
            }

            // Get the blob from response
            const blob = await response.blob();
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `treegress-extension-${environment}.zip`;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading extension:', error);
            alert('Failed to download extension. Please try again.');
        }
    };

    const handleNext = () => {
        // Production users install from the Store, then return to the journeys page without an auto-open modal.
        if (isProduction) {
            navigate('/test-journeys');
            return;
        }

        navigate('/test-journeys', { state: { openCreateModal: true } });
    };

    return (
        <Container maxWidth="md" className="chrome-extension-setup">
            <Box className="setup-content">
                <Typography variant="h4" component="h1" className="setup-title">
                    Set up the Treegress Chrome Extension
                </Typography>
                
                <Typography variant="body1" className="setup-description">
                    Use the extension to record actions in your browser and send them to Treegress.
                </Typography>
                
                {!isProduction && (
                    <Typography variant="body1" className="setup-description">
                        Choose the build that matches the environment you use.
                    </Typography>
                )}

                <Paper className="download-section" elevation={2}>
                    <Typography variant="h6" className="section-title">
                        {isProduction ? 'Install Extension' : 'Download Extension'}
                    </Typography>

                    {isProduction ? (
                        <Button
                            variant="contained"
                            color="primary"
                            href={CHROME_WEB_STORE_EXTENSION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="store-install-button"
                        >
                            Install Treegress Scenario Builder Extension from Chrome Web Store
                        </Button>
                    ) : (
                        <Box className="download-options">
                            {extensionOptions.map((option) => (
                                <Box
                                    key={option.id}
                                    className={`download-item ${currentEnvironment === option.id ? 'current-env' : ''}`}
                                >
                                    <Box className="download-info">
                                        <Box className="download-header">
                                            <Typography variant="body1" className="download-name">
                                                {option.name}
                                            </Typography>
                                            {currentEnvironment === option.id && (
                                                <Chip
                                                    label="Current Environment"
                                                    color="success"
                                                    size="small"
                                                    className="current-chip"
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" className="download-env" color="textSecondary">
                                            {option.description}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<DownloadIcon sx={{ color: '#fff' }} />}
                                        onClick={() => handleDownload(option.id)}
                                        className="download-button"
                                    >
                                        Download ZIP
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>

                <Paper className="instructions-section" elevation={2}>
                    <Typography variant="h6" className="section-title">
                        How to install the Chrome extension:
                    </Typography>

                    {isProduction ? (
                        <Box className="store-instructions">
                            <Typography variant="body1" className="instruction-text">
                                Click Add to Chrome on the Chrome Web Store page and follow the on-screen instructions.
                            </Typography>
                            <Typography variant="body1" className="instruction-note">
                                Note: You do not need to launch Treegress Scenario Builder separately. It will automatically appear in the bottom-right corner after you select the starting page for recording your journey.
                            </Typography>
                        </Box>
                    ) : (
                        <List className="instructions-list">
                            <ListItem>
                                <ListItemText
                                    primary="1. Download the ZIP file for your environment."
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="2. Unzip the file to a folder on your computer."
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="3. Open Chrome and go to chrome://extensions."
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="4. Turn on Developer mode (top right)."
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary='5. Click "Load unpacked" and select the folder you unzipped.'
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="6. (Optional) Pin the Treegress extension in the Chrome toolbar for quick access."
                                />
                            </ListItem>
                        </List>
                    )}
                </Paper>

                <Box className="next-section">
                    <Typography variant="body1" className="next-description">
                        Click the button below once you&apos;ve added the extension and are ready to record your first journey.
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<ArrowForwardIcon sx={{ color: '#fff' }} />}
                        onClick={handleNext}
                        className="next-button"
                    >
                        Next
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default ChromeExtensionSetup;
